import TileWrapper from '../TileWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useOverviewData } from 'app/frontend/base/config/queries'

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { cn } from '@/lib/utils'
import GearStatus from 'app/frontend/types/custom/GearStatus'
import { useTile } from '../../context'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { itemSlotLabels } from 'app/frontend/types/custom/ItemSlot'

type MetricRowProps = {
  label: string
  metric: GearStatus[keyof GearStatus]
}

const MetricRow = ({ label, metric }: MetricRowProps) => {
  const { tile } = useTile()
  const { team } = useTeam()
  const hasCharacters = metric.characters.length > 0

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger
        className={cn('cursor-default', hasCharacters && 'cursor-pointer')}
      >
        <li
          className={cn(
            'bg-background py-2 px-3 rounded-md',
            tile.position === 'main' && 'flex flex-col items-center gap-1',
          )}
        >
          <strong
            className={cn(
              'inline-block mr-1',
              metric.total === 0 ? 'text-primary' : 'text-red-500',
              tile.position === 'main' && 'text-2xl',
            )}
          >
            {metric.total}
          </strong>{' '}
          <span>{label}</span>
        </li>
      </HoverCardTrigger>
      {hasCharacters && (
        <HoverCardContent
          side={tile.position === 'main' ? 'bottom' : 'left'}
          className='max-w-80 w-fit max-h-[40vh] overflow-y-auto'
          align='start'
        >
          <ul className='space-y-4'>
            {metric.characters.map((info) => {
              const character = team.members?.find(
                (member) => member.characterReference.id === info.id,
              )?.characterReference

              if (!character) return null

              return (
                <li
                  key={info.id}
                  className='flex items-center justify-between gap-4'
                >
                  <Link
                    className='flex items-center gap-1.5'
                    to={character.path}
                  >
                    <CharacterClassIcon
                      characterClass={character.characterClass}
                      size='small'
                    />
                    {character.name}
                  </Link>
                  <span className='text-muted-foreground flex flex-wrap gap-1.5'>
                    {info.slots ? (
                      <Badge variant='secondary' className='font-normal'>
                        {info.slots.length > 1
                          ? `${info.slots.length} items`
                          : itemSlotLabels[
                              info.slots[0] as keyof typeof itemSlotLabels
                            ]}
                      </Badge>
                    ) : info.count ? (
                      <Badge variant='secondary' className='font-normal'>
                        {info.count}
                      </Badge>
                    ) : null}
                  </span>
                </li>
              )
            })}
          </ul>
        </HoverCardContent>
      )}
    </HoverCard>
  )
}

const EnchantAuditTile = () => {
  const { team } = useTeam()
  const { tile } = useTile()
  const { data: overviewData } = useOverviewData(team.id)
  const enchantData = overviewData?.enchantData as GearStatus | undefined

  if (!enchantData) {
    return (
      <TileWrapper>
        <p className='text-muted-foreground'>No enchant data available.</p>
      </TileWrapper>
    )
  }

  return (
    <TileWrapper>
      <ul
        className={cn(
          'gap-2',
          tile.position === 'main' ? 'grid grid-cols-4' : 'flex flex-col',
        )}
      >
        <MetricRow
          label='enchants missing'
          metric={
            tile.config.leniency === 'strict'
              ? enchantData.enchants_not_bis
              : enchantData.enchants_missing
          }
        />

        <MetricRow label='empty sockets' metric={enchantData.empty_sockets} />

        <MetricRow
          label='jewelry sockets missing'
          metric={enchantData.jewelry_sockets_missing}
        />

        <MetricRow
          label='embellishments missing'
          metric={enchantData.embellishments}
        />
      </ul>
    </TileWrapper>
  )
}

export default EnchantAuditTile
